import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, Clock } from 'lucide-react';
import { Button } from '../../../core/shared/components/Button.js';

export function UitnodigingPublicPage() {
  const { hash } = useParams();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState<'geaccepteerd' | 'geweigerd' | null>(null);

  useEffect(() => {
    fetch(`/api/uitnodiging/${hash}`)
      .then(async res => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.message || 'Uitnodiging niet gevonden');
        }
        return res.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [hash]);

  const respond = async (actie: 'accepteren' | 'weigeren') => {
    setResponding(true);
    try {
      const res = await fetch(`/api/uitnodiging/${hash}/${actie}`, { method: 'POST' });
      if (!res.ok) throw new Error('Fout bij verwerken');
      setResponded(actie === 'accepteren' ? 'geaccepteerd' : 'geweigerd');
    } catch {
      setError('Er ging iets mis. Probeer opnieuw.');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Uitnodiging niet geldig</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (responded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className={`w-16 h-16 ${responded === 'geaccepteerd' ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {responded === 'geaccepteerd' ? <Check size={32} className="text-green-500" /> : <X size={32} className="text-red-500" />}
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {responded === 'geaccepteerd' ? 'Uitnodiging geaccepteerd!' : 'Uitnodiging geweigerd'}
          </h1>
          <p className="text-gray-500">
            {responded === 'geaccepteerd'
              ? 'Bedankt! U wordt binnenkort gecontacteerd.'
              : 'We hebben uw antwoord geregistreerd.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Uitnodiging</h1>
          <p className="text-gray-500">U bent uitgenodigd voor het volgende dossier</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
          <p className="text-sm"><span className="text-gray-500">Dossier:</span> <span className="font-medium">{data.dossier?.dossierNummer}</span></p>
          <p className="text-sm"><span className="text-gray-500">Klant:</span> <span className="font-medium">{data.dossier?.klantNaam}</span></p>
          <p className="text-sm"><span className="text-gray-500">Adres:</span> <span className="font-medium">{data.dossier?.adres}, {data.dossier?.postcode} {data.dossier?.stad}</span></p>
        </div>

        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">Uitgenodigd als: <span className="font-medium">{data.naam}</span></p>
          <p className="text-xs text-gray-400 flex items-center justify-center gap-1 mt-1">
            <Clock size={12} /> Geldig tot {new Date(data.verlooptOp).toLocaleDateString('nl-BE')}
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={() => respond('weigeren')} disabled={responding}>
            <X size={16} className="mr-1" /> Weigeren
          </Button>
          <Button className="flex-1" onClick={() => respond('accepteren')} disabled={responding}>
            <Check size={16} className="mr-1" /> Accepteren
          </Button>
        </div>
      </div>
    </div>
  );
}
